import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, Search, Star, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@nexus/ui";
import { industryBrandCountsQueryOptions } from "@/lib/queries";
import {
  selectAssignableIndustries,
  sortIndicesByIndustryTitle,
} from "@/features/identity-hub/lib/assignable-industries";
import { orgInitials } from "@/features/identity-hub/utils/iam.utils";
import { IndustryDefaultBrandSelect } from "./IndustryDefaultBrandSelect";
import type { IndustryItem } from "@/features/identity-hub/api/identity-hub.api";
import type { IndustryDefaultBrandMap } from "@/features/identity-hub/lib/org-settings-dirty";

export interface OrgIndustriesSettingsTableProps {
  selected: string[];
  defaultIndustry: string | null;
  industryDefaultBrands: IndustryDefaultBrandMap;
  industries: IndustryItem[];
  disabled?: boolean;
  brandSelectEnabled: boolean;
  onSelectedChange: (indices: string[]) => void;
  onDefaultIndustryChange: (index: string | null) => void;
  onIndustryDefaultBrandChange: (industry: string, brand: string) => void;
}

const TABLE_GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_minmax(0,200px)_84px_28px] items-center gap-3 px-3";

interface IndustryBrandCountProps {
  count: number;
  isLoading: boolean;
  isError: boolean;
  className?: string;
}

function IndustryBrandCount({ count, isLoading, isError, className }: IndustryBrandCountProps) {
  const { t } = useTranslation();

  const label = isLoading
    ? t("iam.orgDrawer.settings.industriesBrandCountLoading", "Loading brands…")
    : isError
      ? t("iam.orgDrawer.settings.industriesBrandCountError", "Brand count unavailable")
      : t("iam.orgDrawer.settings.industriesBrandCount", {
          count,
          defaultValue_one: "{{count}} brand tracked",
          defaultValue_other: "{{count}} brands tracked",
        });

  return <span className={cn("block text-[10px] leading-[1.4] text-muted-foreground", className)}>{label}</span>;
}

export function OrgIndustriesSettingsTable({
  selected,
  defaultIndustry,
  industryDefaultBrands,
  industries,
  disabled = false,
  brandSelectEnabled,
  onSelectedChange,
  onDefaultIndustryChange,
  onIndustryDefaultBrandChange,
}: OrgIndustriesSettingsTableProps) {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const brandCounts = useQuery(
    industryBrandCountsQueryOptions({ enabled: brandSelectEnabled && (selected.length > 0 || addOpen) }),
  );

  const titleByIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const ind of industries) map.set(ind.index, ind.title);
    return map;
  }, [industries]);

  const availableToAdd = useMemo(
    () => selectAssignableIndustries(industries).filter((ind) => !selected.includes(ind.index)),
    [industries, selected],
  );

  const sortedSelected = useMemo(
    () => sortIndicesByIndustryTitle(selected, titleByIndex),
    [selected, titleByIndex],
  );

  const effectiveDefault =
    defaultIndustry && selected.includes(defaultIndustry)
      ? defaultIndustry
      : selected.length === 1
        ? selected[0]!
        : null;

  const handleRemove = (index: string) => {
    const next = selected.filter((i) => i !== index);
    onSelectedChange(next);
    if (defaultIndustry === index) {
      onDefaultIndustryChange(next.length === 1 ? next[0]! : null);
    }
  };

  const handleAdd = (index: string) => {
    if (selected.includes(index)) return;
    const next = [...selected, index];
    onSelectedChange(next);
    if (next.length === 1) onDefaultIndustryChange(index);
    setAddOpen(false);
  };

  return (
    <div
      className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border"
      data-testid="org-settings-industries-table"
    >
      {/* One region for every count label — a live region per row or menu item would announce them all at once. */}
      <span role="status" aria-live="polite" className="sr-only">
        {brandCounts.isLoading
          ? t("iam.orgDrawer.settings.industriesBrandCountLoading", "Loading brands…")
          : brandCounts.isError
            ? t("iam.orgDrawer.settings.industriesBrandCountError", "Brand count unavailable")
            : ""}
      </span>
      {selected.length > 0 ? (
        <>
          <div
            className={cn(
              TABLE_GRID_CLASS,
              "border-b border-border bg-muted py-2 text-[10px] uppercase leading-none tracking-[0.4px] text-muted-foreground",
            )}
            aria-hidden
          >
            <span>{t("iam.orgDrawer.settings.industriesTableIndustryColumn", "Industry")}</span>
            <span>{t("iam.orgDrawer.settings.industriesTableMyBrandColumn", "Default My Brand")}</span>
            <span>{t("iam.orgDrawer.settings.industriesTableDefaultColumn", "Default")}</span>
            <span />
          </div>
          <ul className="divide-y divide-border-subtle border-b border-border-subtle">
            {sortedSelected.map((index) => {
              const title = titleByIndex.get(index) ?? index;
              const isDefault = effectiveDefault === index;

              return (
                <li
                  key={index}
                  data-testid={`org-settings-industry-row-${index}`}
                  className={cn(TABLE_GRID_CLASS, "py-2.5", isDefault && "bg-primary-subtle")}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="flex size-7 shrink-0 items-center justify-center rounded-md bg-border-subtle font-mono text-[10px] font-medium text-muted-foreground"
                      aria-hidden
                    >
                      {orgInitials(title)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-medium leading-[1.4] text-foreground">{title}</p>
                      <IndustryBrandCount
                        count={brandCounts.data?.[index] ?? 0}
                        isLoading={brandCounts.isLoading}
                        isError={brandCounts.isError}
                      />
                    </div>
                  </div>
                  <IndustryDefaultBrandSelect
                    variant="table"
                    industry={index}
                    industryTitle={title}
                    value={industryDefaultBrands[index] ?? ""}
                    onChange={(brand) => onIndustryDefaultBrandChange(index, brand)}
                    disabled={disabled}
                    enabled={brandSelectEnabled}
                  />
                  <div>
                    {isDefault ? (
                      <span className="inline-flex h-6 items-center gap-1 rounded-full border border-primary/30 bg-background px-2 text-[11px] font-medium leading-none text-primary-subtle-foreground">
                        <Star className="size-3 fill-primary text-primary" aria-hidden />
                        {t("iam.orgDrawer.settings.industriesDefaultPill", "Default")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={disabled || selected.length < 2}
                        onClick={() => onDefaultIndustryChange(index)}
                        aria-label={t("iam.orgDrawer.settings.industriesMakeDefaultAria", {
                          industry: title,
                          defaultValue: "Make {{industry}} the default",
                        })}
                        className="inline-flex h-6 items-center gap-1 rounded-md px-1 text-[11px] leading-none text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Star className="size-3" aria-hidden />
                        {t("iam.orgDrawer.settings.industriesMakeDefault", "Set")}
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => handleRemove(index)}
                    data-testid={`org-settings-industry-remove-${index}`}
                    aria-label={t("iam.orgDrawer.settings.industriesRemoveAria", {
                      industry: title,
                      defaultValue: "Remove {{industry}}",
                    })}
                    className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p className="border-b border-border-subtle px-3 py-4 text-center text-[12px] text-muted-foreground">
          {t("iam.orgDrawer.settings.industriesEmpty", "No industries assigned yet.")}
        </p>
      )}

      <Popover open={addOpen} onOpenChange={disabled ? undefined : setAddOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled || availableToAdd.length === 0}
            data-testid="org-settings-industries-add"
            className={cn(
              "flex h-10 w-full items-center gap-2.5 bg-background px-3 text-left text-[13px] text-muted-foreground outline-none transition-colors",
              "hover:bg-muted/30 focus-visible:bg-muted/30 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring/40",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <Search className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">
              {t(
                "iam.orgDrawer.settings.industriesAddPlaceholder",
                "Add an industry — type to search the catalog…",
              )}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t("iam.orgDrawer.settings.industriesSearchPlaceholder", "Search industries…")}
            />
            <CommandList>
              <CommandEmpty>{t("iam.orgDrawer.settings.industriesSearchEmpty", "No industries found.")}</CommandEmpty>
              <CommandGroup>
                {availableToAdd.map((ind) => (
                  <CommandItem key={ind.index} value={ind.title} onSelect={() => handleAdd(ind.index)}>
                    <Check className="mr-2 size-3.5 opacity-0" />
                    <span className="min-w-0 flex-1 truncate">{ind.title}</span>
                    <IndustryBrandCount
                      count={brandCounts.data?.[ind.index] ?? 0}
                      isLoading={brandCounts.isLoading}
                      isError={brandCounts.isError}
                      className="ml-3 shrink-0"
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
